import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/types/supabase';

type Product = Database['public']['Tables']['products']['Row'];

/**
 * Service per la gestione prodotti con funzionalità avanzate:
 * - Generazione automatica SKU
 * - Generazione automatica barcode EAN-13
 * - Upload video prodotti (max 1MB)
 */

// =====================================================
// GENERAZIONE SKU AUTOMATICA
// =====================================================

export interface GenerateSKUOptions {
  organizationId: string;
  prefix?: string; // Default: 'PROD'
  maxRetries?: number; // Default: 5
  _retryCount?: number; // Internal counter
}

export interface SKUResult {
  sku: string;
  error?: string;
}

async function nextSkuAfterHighest(organizationId: string, prefix: string): Promise<string | null> {
  const { data } = await supabase
    .from('products')
    .select('sku')
    .eq('organization_id', organizationId)
    .like('sku', `${prefix}-%`)
    .order('sku', { ascending: false })
    .limit(500);
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`);
  let max = 0;
  let width = 5;
  for (const row of (data || []) as { sku: string | null }[]) {
    const m = row.sku ? re.exec(row.sku) : null;
    if (!m) continue;
    max = Math.max(max, parseInt(m[1], 10));
    width = Math.max(width, m[1].length);
  }
  if (max === 0) return null;
  const candidate = `${prefix}-${String(max + 1).padStart(width, '0')}`;
  return (await checkSKUExists(organizationId, candidate)) ? null : candidate;
}

/**
 * Genera il prossimo SKU automatico per l'organizzazione
 * Formato: PREFIX-00001 (es: PROD-00001, PROD-00002)
 */
export async function generateNextSKU(options: GenerateSKUOptions): Promise<SKUResult> {
  try {
    const { organizationId, prefix = 'PROD', maxRetries = 5, _retryCount = 0 } = options;

    // Verifica limite retry
    if (_retryCount >= maxRetries) {
      return {
        sku: '',
        error: `Max retry limit reached (${maxRetries}). Unable to generate unique SKU.`
      };
    }

    // Chiama la funzione database per generare SKU
    const { data, error } = await supabase.rpc('generate_next_sku', {
      org_id: organizationId,
      sku_prefix: prefix
    });

    if (error) {
      console.error('Error generating SKU:', error);
      return { sku: '', error: error.message };
    }

    // Verifica che lo SKU generato non esista già (safety check)
    const { data: existingProduct } = await supabase
      .from('products')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('sku', data)
      .maybeSingle();

    if (existingProduct) {
      // Il contatore del DB parte da 0 anche se i prodotti importati hanno già codici alti:
      // invece di scorrere un codice alla volta, si riparte dal più alto già presente.
      const fromHighest = await nextSkuAfterHighest(organizationId, prefix);
      if (fromHighest) return { sku: fromHighest };
      console.warn(`Generated SKU already exists, retrying... (attempt ${_retryCount + 1}/${maxRetries})`);
      return generateNextSKU({
        ...options,
        _retryCount: _retryCount + 1
      });
    }

    return { sku: data };
  } catch (error) {
    console.error('Exception generating SKU:', error);
    return {
      sku: '',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Verifica se uno SKU è già utilizzato nell'organizzazione
 */
export async function checkSKUExists(organizationId: string, sku: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('sku', sku)
      .maybeSingle();

    if (error) {
      console.error('Error checking SKU:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Exception checking SKU:', error);
    return false;
  }
}

// =====================================================
// GENERAZIONE BARCODE EAN-13
// =====================================================

export interface BarcodeOptions {
  organizationId: string;
  maxRetries?: number; // Default: 5
  _retryCount?: number; // Internal counter
}

export interface BarcodeResult {
  barcode: string;
  error?: string;
}

/**
 * Genera il prossimo barcode EAN-13 per l'organizzazione
 * Formato: 8001234567890 (13 cifre con check digit)
 * Country code: 800-839 (per uso interno)
 */
export async function generateNextEAN13Barcode(options: BarcodeOptions): Promise<BarcodeResult> {
  try {
    const { organizationId, maxRetries = 5, _retryCount = 0 } = options;

    // Verifica limite retry
    if (_retryCount >= maxRetries) {
      return {
        barcode: '',
        error: `Max retry limit reached (${maxRetries}). Unable to generate unique barcode.`
      };
    }

    // Chiama la funzione database per generare barcode EAN-13
    const { data, error } = await supabase.rpc('generate_next_ean13_barcode', {
      org_id: organizationId
    });

    if (error) {
      console.error('Error generating barcode:', error);
      return { barcode: '', error: error.message };
    }

    // Verifica che il barcode generato non esista già (safety check)
    const { data: existingProduct } = await supabase
      .from('products')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('barcode', data)
      .maybeSingle();

    if (existingProduct) {
      // Barcode duplicato, riprova (dovrebbe essere rarissimo)
      console.warn(`Generated barcode already exists, retrying... (attempt ${_retryCount + 1}/${maxRetries})`);
      return generateNextEAN13Barcode({
        ...options,
        _retryCount: _retryCount + 1
      });
    }

    return { barcode: data };
  } catch (error) {
    console.error('Exception generating barcode:', error);
    return {
      barcode: '',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Verifica se un barcode è già utilizzato nell'organizzazione
 */
export async function checkBarcodeExists(organizationId: string, barcode: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('barcode', barcode)
      .maybeSingle();

    if (error) {
      console.error('Error checking barcode:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Exception checking barcode:', error);
    return false;
  }
}

/**
 * Valida formato barcode EAN-13 (13 cifre con check digit valido)
 */
export function validateEAN13Barcode(barcode: string): boolean {
  // Deve essere esattamente 13 cifre
  if (!/^\d{13}$/.test(barcode)) {
    return false;
  }

  // Calcola check digit
  const digits = barcode.split('').map(Number);
  const base = digits.slice(0, 12);
  const checkDigit = digits[12];

  let sumOdd = 0;
  let sumEven = 0;

  for (let i = 0; i < 12; i++) {
    if (i % 2 === 0) {
      sumOdd += base[i];
    } else {
      sumEven += base[i] * 3;
    }
  }

  const total = sumOdd + sumEven;
  const calculatedCheckDigit = (10 - (total % 10)) % 10;

  return calculatedCheckDigit === checkDigit;
}

// =====================================================
// UPLOAD VIDEO PRODOTTI
// =====================================================

export interface VideoUploadOptions {
  file: File;
  productId?: string; // Se specificato, usa questo come filename
  organizationId: string;
}

export interface VideoUploadResult {
  url: string;
  filename: string;
  sizeBytes: number;
  error?: string;
}

const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB in bytes (Supabase global limit)
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
const VIDEO_BUCKET = 'product-videos';

/**
 * Upload video prodotto su Supabase Storage
 * Limitazioni: max 50MB, formati: mp4, webm, mov, avi
 * Path structure: {organizationId}/{productId}/video_{timestamp}.{ext}
 */
export async function uploadProductVideo(options: VideoUploadOptions): Promise<VideoUploadResult> {
  try {
    const { file, productId, organizationId } = options;

    // Validazione dimensione file
    if (file.size > MAX_VIDEO_SIZE) {
      return {
        url: '',
        filename: '',
        sizeBytes: 0,
        error: `Il video è troppo grande. Dimensione massima: 50MB (caricato: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
      };
    }

    // Validazione tipo file
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      return {
        url: '',
        filename: '',
        sizeBytes: 0,
        error: `Formato video non supportato. Formati consentiti: MP4, WebM, MOV, AVI`
      };
    }

    // Genera filename univoco con struttura organizzata per org/product
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop() || 'mp4';
    const filename = `video_${timestamp}.${fileExt}`;

    // Path: {organizationId}/{productId}/video_{timestamp}.{ext}
    // Se productId non è disponibile (nuovo prodotto), usa 'temp' come folder
    const productFolder = productId || 'temp';
    const filePath = `${organizationId}/${productFolder}/${filename}`;

    // Upload a Supabase Storage
    const { data, error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });

    if (error) {
      console.error('Error uploading video:', error);
      return {
        url: '',
        filename: '',
        sizeBytes: 0,
        error: error.message
      };
    }

    // Ottieni URL pubblico
    const { data: urlData } = supabase.storage
      .from(VIDEO_BUCKET)
      .getPublicUrl(filePath);

    return {
      url: urlData.publicUrl,
      filename: file.name,
      sizeBytes: file.size
    };
  } catch (error) {
    console.error('Exception uploading video:', error);
    return {
      url: '',
      filename: '',
      sizeBytes: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Elimina video prodotto da Supabase Storage
 */
export async function deleteProductVideo(videoUrl: string, organizationId: string): Promise<boolean> {
  try {
    // Estrai path dal URL pubblico
    const urlParts = videoUrl.split(`${VIDEO_BUCKET}/`);
    if (urlParts.length < 2) {
      console.error('Invalid video URL format');
      return false;
    }

    const filePath = urlParts[1];

    // Verifica che il path inizi con organizationId (security)
    if (!filePath.startsWith(organizationId)) {
      console.error('Unauthorized: video does not belong to organization');
      return false;
    }

    // Elimina da Storage
    const { error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting video:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception deleting video:', error);
    return false;
  }
}

/**
 * Aggiorna dati video nel record prodotto
 */
export async function updateProductVideoData(
  productId: string,
  videoData: {
    video_url?: string | null;
    video_filename?: string | null;
    video_size_bytes?: number | null;
  }
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('products')
      .update(videoData)
      .eq('id', productId);

    if (error) {
      console.error('Error updating product video data:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception updating product video data:', error);
    return false;
  }
}

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Formatta dimensione file in formato leggibile
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Verifica se il video è sotto il limite di dimensione
 */
export function isVideoSizeValid(sizeBytes: number): boolean {
  return sizeBytes <= MAX_VIDEO_SIZE;
}

/**
 * Ottiene informazioni sul limite video
 */
export function getVideoLimits() {
  return {
    maxSizeBytes: MAX_VIDEO_SIZE,
    maxSizeFormatted: formatFileSize(MAX_VIDEO_SIZE),
    allowedTypes: ALLOWED_VIDEO_TYPES,
    allowedExtensions: ['mp4', 'webm', 'mov', 'avi']
  };
}

// =====================================================
// UPLOAD FILE SCARICABILI PRODOTTI
// =====================================================

const FILE_BUCKET = 'product-files';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface FileUploadOptions {
  file: File;
  productId?: string;
  organizationId: string;
  /** Limite per questo caricamento; default MAX_FILE_SIZE (10 MB). */
  maxSizeBytes?: number;
}

export interface FileUploadResult {
  url: string;
  filename: string;
  originalName: string;
  sizeBytes: number;
  mimeType: string;
  error?: string;
}

/**
 * Upload file scaricabile prodotto su Supabase Storage
 * Limitazioni: max 10MB, qualsiasi tipo di file
 * Path structure: {organizationId}/{productId}/{timestamp}_{filename}
 */
export async function uploadProductFile(options: FileUploadOptions): Promise<FileUploadResult> {
  try {
    const { file, productId, organizationId, maxSizeBytes = MAX_FILE_SIZE } = options;

    // Validazione dimensione file
    if (file.size > maxSizeBytes) {
      return {
        url: '',
        filename: '',
        originalName: '',
        sizeBytes: 0,
        mimeType: '',
        error: `Il file è troppo grande. Dimensione massima: ${Math.round(maxSizeBytes / 1024 / 1024)}MB (caricato: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
      };
    }

    // Genera filename univoco
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filename = `${timestamp}_${sanitizedName}`;

    // Path: {organizationId}/{productId}/{timestamp}_{filename}
    const productFolder = productId || 'temp';
    const filePath = `${organizationId}/${productFolder}/${filename}`;

    // Upload a Supabase Storage
    const { data, error } = await supabase.storage
      .from(FILE_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'application/octet-stream'
      });

    if (error) {
      console.error('Error uploading file:', error);
      return {
        url: '',
        filename: '',
        originalName: '',
        sizeBytes: 0,
        mimeType: '',
        error: error.message
      };
    }

    // Ottieni URL pubblico
    const { data: urlData } = supabase.storage
      .from(FILE_BUCKET)
      .getPublicUrl(filePath);

    return {
      url: urlData.publicUrl,
      filename: filename,
      originalName: file.name,
      sizeBytes: file.size,
      mimeType: file.type || 'application/octet-stream'
    };
  } catch (error) {
    console.error('Exception uploading file:', error);
    return {
      url: '',
      filename: '',
      originalName: '',
      sizeBytes: 0,
      mimeType: '',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Elimina file scaricabile prodotto da Supabase Storage
 */
export async function deleteProductFile(fileUrl: string, organizationId: string): Promise<boolean> {
  try {
    // Estrai path dal URL pubblico
    const urlParts = fileUrl.split(`${FILE_BUCKET}/`);
    if (urlParts.length < 2) {
      console.error('Invalid file URL format');
      return false;
    }

    const filePath = urlParts[1];

    // Verifica che il path inizi con organizationId (security)
    if (!filePath.startsWith(organizationId)) {
      console.error('Unauthorized: file does not belong to organization');
      return false;
    }

    // Elimina da Storage
    const { error } = await supabase.storage
      .from(FILE_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception deleting file:', error);
    return false;
  }
}

/**
 * Ottiene informazioni sui limiti file
 */
export function getFileLimits() {
  return {
    maxSizeBytes: MAX_FILE_SIZE,
    maxSizeFormatted: formatFileSize(MAX_FILE_SIZE)
  };
}

// =====================================================
// EXPORT DEFAULT
// =====================================================

export const productService = {
  // SKU
  generateNextSKU,
  checkSKUExists,

  // Barcode
  generateNextEAN13Barcode,
  checkBarcodeExists,
  validateEAN13Barcode,

  // Video
  uploadProductVideo,
  deleteProductVideo,
  updateProductVideoData,

  // File scaricabili
  uploadProductFile,
  deleteProductFile,
  getFileLimits,

  // Utility
  formatFileSize,
  isVideoSizeValid,
  getVideoLimits
};
